import React from "react";
import { Skeleton } from "@flixbus/honeycomb-react";
import {
  operators,
  type PaletteVariable,
  type TokenKind,
} from "../FormulaEditor.types";
import styles from "./Palette.module.scss";
import PaletteChip from "../PaletteChip/PaletteChip.component";

interface PaletteProps {
  kamVariables: PaletteVariable[];
  opDataVariables: PaletteVariable[];
  isLoading: boolean;
  onAdd: (kind: TokenKind, value: string) => void;
}

// Widths approximate real chip labels so the skeleton occupies the same space
// the loaded chips will, avoiding a layout jump when the pool resolves.
const skeletonChipWidths = [
  "sm",
  "xs",
  "md",
  "xs",
  "sm",
  "xs",
  "md",
  "xs",
] as const;

const Palette: React.FC<PaletteProps> = ({
  kamVariables,
  opDataVariables,
  isLoading,
  onAdd,
}) => (
  <div className={styles.palette}>
    <div>
      <div className={styles.paletteSectionLabel}>Operators</div>
      <div className={styles.paletteChips}>
        {operators.map((op) => (
          <PaletteChip
            key={op.value}
            id={`palette-op-${op.value}`}
            kind={op.kind}
            value={op.value}
            label={op.label}
            onAdd={onAdd}
          />
        ))}
      </div>
    </div>

    <div>
      <div className={styles.paletteSectionLabel}>Variables</div>
      {isLoading ? (
        <div className={styles.paletteChips} aria-busy="true">
          {skeletonChipWidths.map((width, index) => (
            <Skeleton
              key={index}
              inline
              round
              flushBottom
              width={width}
              height="md"
            />
          ))}
        </div>
      ) : (
        <div className={styles.paletteChips}>
          {kamVariables.map((variable) => (
            <PaletteChip
              key={variable.code}
              id={`palette-var-${variable.code}`}
              kind="var"
              value={variable.code}
              label={variable.name}
              onAdd={onAdd}
            />
          ))}
        </div>
      )}
    </div>

    {(isLoading || opDataVariables.length > 0) && (
      <div>
        <div className={styles.paletteSectionLabel}>Operational Data</div>
        {isLoading ? (
          <div className={styles.paletteChips} aria-busy="true">
            {skeletonChipWidths.map((width, index) => (
              <Skeleton
                key={index}
                inline
                round
                flushBottom
                width={width}
                height="md"
              />
            ))}
          </div>
        ) : (
          <div className={styles.paletteChips}>
            {opDataVariables.map((variable) => (
              <PaletteChip
                key={variable.code}
                id={`palette-sysvar-${variable.code}`}
                kind="var"
                value={variable.code}
                label={variable.name}
                onAdd={onAdd}
              />
            ))}
          </div>
        )}
      </div>
    )}

    <span className={styles.paletteTip}>
      {"Tip: click any element to add it to the formula"}
    </span>
  </div>
);

export default Palette;
